import { Component, type ReactElement } from "react";

import { type ListingMapEntry } from "@/graphql.ts";
import { MAX_NUMBER_OF_CONTENT_BROWSER_ENTRIES } from "@/settings/settings.ts";

import { type INavPanelCategory } from "../core/navigation-panel/NavigationPanel.js";
import { type IRouteNavItemProps } from "../core/navigation-panel/RouteNavItem.js";
import { ContentBrowserChartBar } from "./ContentBrowserChartBar.js";

export interface IContentBrowserChartBarsProps {
  data: ListingMapEntry[];
  selectedEntry: ListingMapEntry;
  selectedContentBrowserFeature: INavPanelCategory;
  selectedContentBrowserAttribute: IRouteNavItemProps;
  totalCount: number;

  changeSelection: (entry: ListingMapEntry) => void;
  getBarTooltip?: (entry: ListingMapEntry) => string;
  handleEdit?: (entry: ListingMapEntry) => void;
  handleDelete?: (entry: ListingMapEntry) => void;
  editDisabledTooltip?: (entry: ListingMapEntry) => string; // callback to get the disabled tooltip depending on the entry
  deleteDisabledTooltip?: (entry: ListingMapEntry) => string; // callback to get the disabled tooltip depending on the entry
}

interface IContentBrowserChartState {
  minorEntriesExpanded: boolean;
}

/**
 * Content Browser panel where the group by attribute values are shown as chart bars.
 * If there are too many entries we collapse all the 'minor' ones into a single one called 'others'
 * and when clicking on this row all will be visible.
 *
 * created on 5/16/2017
 */
class ContentBrowserChartBars extends Component<IContentBrowserChartBarsProps, IContentBrowserChartState> {
  constructor(props: IContentBrowserChartBarsProps) {
    super(props);

    this.state = {
      minorEntriesExpanded: false,
    };
  }

  render() {
    return <div>{this.renderContentBrowserBars()}</div>;
  }

  renderContentBrowserBars(): ReactElement[] {
    //const maxCount: number = this.props.data && maxBy(this.props.data, 'value').value
    const total: number = this.props.totalCount;
    // largest single value, not accounting for the 'Others' which is the sum of all initially invisible entry values.
    let sumOfOtherEntries = 0;
    let bars: ReactElement[] = [];

    if (this.props.data) {
      bars = this.props.data.map((entry: ListingMapEntry, index: number) => {
        if (
          !this.state.minorEntriesExpanded &&
          index >= MAX_NUMBER_OF_CONTENT_BROWSER_ENTRIES &&
          this.props.data.length > MAX_NUMBER_OF_CONTENT_BROWSER_ENTRIES + 1
        ) {
          sumOfOtherEntries += entry.value;
          return null;
        } else {
          return (
            <ContentBrowserChartBar
              key={`content-browser-bar-${entry.key}`}
              entry={entry}
              selected={this.props.selectedEntry && this.props.selectedEntry.key === entry.key}
              proportion={(entry.value / total) * 100}
              selectedContentBrowserFeature={this.props.selectedContentBrowserFeature}
              selectedContentBrowserAttribute={this.props.selectedContentBrowserAttribute}
              onBarClicked={this.props.changeSelection}
              getBarTooltip={this.props.getBarTooltip}
              handleEdit={this.props.handleEdit}
              handleDelete={this.props.handleDelete}
              editDisabledTooltip={this.props.editDisabledTooltip}
              deleteDisabledTooltip={this.props.deleteDisabledTooltip}
            />
          );
        }
      });

      // other entries beyond the 6 first entries (even only having count = 0)
      if (!this.state.minorEntriesExpanded && this.props.data.length > MAX_NUMBER_OF_CONTENT_BROWSER_ENTRIES) {
        const othersEntry: ListingMapEntry = {
          key: "others",
          label: "Others",
          value: sumOfOtherEntries,
          columnField: null,
          tableFilter: null,
        };

        // Show a bar for the 'Others', but limit the length to the longest initially visible value.
        // Toggle visibility for the 'Others'.
        // TBD: should the 'Others' bar show some kind of an ellipsis if the value is restricted in length?
        bars.push(
          <ContentBrowserChartBar
            title="Show others"
            key={`content-browser-bar-others`}
            entry={othersEntry}
            selected={false}
            proportion={(Math.min(othersEntry.value, total) / total) * 100}
            selectedContentBrowserFeature={this.props.selectedContentBrowserFeature}
            selectedContentBrowserAttribute={this.props.selectedContentBrowserAttribute}
            onBarClicked={() => this.setState({ minorEntriesExpanded: true })}
          />,
        );
      }
    }
    return bars;
  }
}

export default ContentBrowserChartBars;
